/* eslint-disable camelcase */
import React, { useState, ElementType } from 'react';
import {
  Button,
  Flex,
  Input,
  useToast,
  VStack,
  useColorModeValue,
  Icon,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import Select, { SingleValue } from 'react-select';
import { useInsertCarrierOneMutation, useSearchUserWithoutCarrierLazyQuery } from '../utils/__generated__/graphql';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { PageHeader, ModernCard } from './shared';

export const AddNewCarrier = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [insertCarrier] = useInsertCarrierOneMutation();
  const [name, setName] = useState('');
  const [nameValid, setNameValid] = useState(true);
  const [value, setValue] = useState<{ id: string; email: string } | undefined>({ id: '', email: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const accentColor = useColorModeValue('brand.500', 'brand.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [search, { loading, data }] = useSearchUserWithoutCarrierLazyQuery({
    variables: { searchTerm },
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const options =
    data?.users.map((result) => ({
      value: {
        id: result.id,
        email: result.email,
      },
      label: result.email,
    })) || [];

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameValid(e.target.value.length > 3); // Update validity based on length
  };

  const handleSelectChange = (newValue: string) => {
    setSearchTerm(newValue);
    search({ variables: { searchTerm: newValue } });
  };

  const handleClick = (opt: SingleValue<{ value: { id: string; email: string }; label: string }>) => {
    setValue(opt?.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log(value);

    if (name.length === 0 || value?.email.length === 0) {
      toast({
        title: 'Name/Email/Phone is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const { data } = await insertCarrier({
        variables: {
          insert_carrier_inputs: {
            userId: value?.id,
            name: name,
          },
        },
      });

      toast({
        title: 'carrier added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/carrier/edit/${data?.insert_carrier_one?.id}`);
    } catch (error: any) {
      console.error('Error adding carrier:', error?.message);
      toast({
        title: 'Failed to add carrier',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Add New Carrier"
        subtitle="Create a new carrier profile"
        actions={
          <Button
            leftIcon={<Icon as={FiArrowLeft as ElementType} />}
            colorScheme="gray"
            variant="outline"
            onClick={() => navigate(-1)}
            size={{ base: 'md', md: 'lg' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Back to Carriers</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Back</Text>
          </Button>
        }
      />

      <ModernCard p={{ base: 6, md: 8 }}>
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            <FormControl isInvalid={!nameValid}>
              <FormLabel>Carrier Name</FormLabel>
              <Input
                placeholder="Enter carrier name"
                value={name}
                onChange={handleNameChange}
                size="lg"
                _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
              />
              <FormErrorMessage>Carrier name must be longer than 3 characters</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Carrier User</FormLabel>
              <Select
                options={options}
                isLoading={loading}
                isClearable
                isSearchable
                onInputChange={handleSelectChange}
                onChange={(v) => handleClick(v)}
                placeholder="Search for carrier user..."
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '40px',
                    borderColor: borderColor,
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: useColorModeValue('gray.300', 'gray.600'),
                    },
                  }),
                }}
              />
            </FormControl>
          </VStack>

          <Flex mt={8} justifyContent="flex-end">
            <Button
              type="submit"
              colorScheme="brand"
              size="lg"
              leftIcon={<Icon as={FiPlus as ElementType} />}
              isDisabled={!nameValid || !value?.email}
              _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Add Carrier</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Add</Text>
            </Button>
          </Flex>
        </form>
      </ModernCard>
    </>
  );
};
